// The television's picture tube: a bulging sheet of glass at the room's
// tvScreen anchor that shows a canvas the way a CRT does. The picture is
// drawn in rows with dark gaps between them, each row split into phosphor
// stripes; the corners are rounded and darker, the glass carries a soft
// reflection, and a change of channel floods it with noise for a moment.

import * as THREE from "three";
import type { RoomManifest } from "../room/contract.ts";
import { LAYER_SELECT } from "./device.ts";

const VERTEX = /* glsl */ `
varying vec2 vUv;
void main() {
  vUv = uv;
  gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
}
`;

const FRAGMENT = /* glsl */ `
uniform sampler2D map;
uniform vec2 lines;     // logical columns and rows of the picture
uniform float time;     // seconds
uniform float snow;     // 0..1: how much of the picture is noise
uniform float power;    // 0 off, 1 on
varying vec2 vUv;

float hash(vec2 p) {
  return fract(sin(dot(p, vec2(12.9898, 78.233))) * 43758.5453);
}

void main() {
  // The tube's face curves away from the viewer toward its edges.
  vec2 c = vUv - 0.5;
  vec2 uv = 0.5 + c * (1.0 + 0.11 * dot(c, c));
  // A rounded rectangle; outside it is the unlit edge of the glass.
  vec2 edge = abs(uv - 0.5) - (0.5 - 0.055);
  float outside = length(max(edge, 0.0)) - 0.055;
  float face = 1.0 - smoothstep(-0.004, 0.004, outside);

  vec3 picture = texture2D(map, uv).rgb;
  float grain = hash(floor(uv * lines) + floor(time * 60.0));
  picture = mix(picture, vec3(grain), snow);

  // Rows: bright through the middle of each, dark between.
  float row = sin(uv.y * lines.y * 3.14159265);
  picture *= 0.58 + 0.42 * row * row;
  // Phosphor stripes, three to a logical column.
  float stripe = fract(uv.x * lines.x);
  vec3 mask = vec3(
    smoothstep(0.0, 0.25, stripe) * (1.0 - smoothstep(0.3, 0.45, stripe)),
    smoothstep(0.28, 0.45, stripe) * (1.0 - smoothstep(0.6, 0.75, stripe)),
    smoothstep(0.6, 0.75, stripe) * (1.0 - smoothstep(0.9, 1.0, stripe)));
  picture *= mix(vec3(1.0), 0.7 + 0.9 * mask, 0.35);
  // A band of brightness rolls down the tube, and the whole picture breathes.
  picture *= 1.0 + 0.045 * sin((uv.y + time * 0.21) * 6.2831853) + 0.012 * sin(time * 53.0);
  picture *= 1.28 * power;

  // Darker toward the corners, with the window's light across the upper glass.
  float vignette = 1.0 - 1.5 * dot(c, c);
  vec3 shown = picture * vignette + vec3(0.03, 0.03, 0.05);
  float sheen = smoothstep(0.55, 0.0, distance(vUv, vec2(0.24, 0.84)));
  shown += vec3(0.42, 0.46, 0.6) * sheen * sheen * 0.16;
  gl_FragColor = vec4(mix(vec3(0.012, 0.012, 0.02), shown, face), 1.0);
  #include <colorspace_fragment>
}
`;

export class TvScreen {
  readonly mesh: THREE.Mesh;
  /** A box around the whole set, so its body selects it too. */
  readonly hitbox: THREE.Mesh;
  readonly centre = new THREE.Vector3();
  /** The top of the set, where its label hangs. */
  readonly head = new THREE.Vector3();
  readonly resolution: [number, number];
  readonly canvas = document.createElement("canvas");
  private texture: THREE.CanvasTexture;
  private uniforms: { map: { value: THREE.Texture }; lines: { value: THREE.Vector2 }; time: { value: number }; snow: { value: number }; power: { value: number } };
  private snowLeft = 0;

  /** `density` is canvas pixels per logical pixel of the picture. */
  constructor(manifest: RoomManifest, density: number) {
    const { corners, resolution, bulge } = manifest.anchors.tvScreen;
    this.resolution = resolution;
    this.canvas.width = resolution[0] * density;
    this.canvas.height = resolution[1] * density;
    const g = this.canvas.getContext("2d")!;
    g.fillStyle = "#171226";
    g.fillRect(0, 0, this.canvas.width, this.canvas.height);

    const [bl, br, tr, tl] = corners.map((corner) => new THREE.Vector3().fromArray(corner));
    const right = new THREE.Vector3().subVectors(br!, bl!);
    const up = new THREE.Vector3().subVectors(tl!, bl!);
    const normal = new THREE.Vector3().crossVectors(right, up).normalize();
    // A grid over the quad, pushed out along the normal in a shallow dome.
    const columns = 20;
    const rows = 15;
    const positions: number[] = [];
    const uvs: number[] = [];
    const indices: number[] = [];
    for (let j = 0; j <= rows; j++) {
      for (let i = 0; i <= columns; i++) {
        const u = i / columns;
        const v = j / rows;
        const dome = bulge * (1 - (2 * u - 1) ** 2) * (1 - (2 * v - 1) ** 2);
        const point = bl!.clone().addScaledVector(right, u).addScaledVector(up, v).addScaledVector(normal, dome);
        positions.push(point.x, point.y, point.z);
        // Canvas rows run top to bottom, so v is flipped against the quad.
        uvs.push(u, 1 - v);
      }
    }
    for (let j = 0; j < rows; j++) {
      for (let i = 0; i < columns; i++) {
        const a = j * (columns + 1) + i;
        indices.push(a, a + 1, a + columns + 2, a, a + columns + 2, a + columns + 1);
      }
    }
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute("position", new THREE.Float32BufferAttribute(positions, 3));
    geometry.setAttribute("uv", new THREE.Float32BufferAttribute(uvs, 2));
    geometry.setIndex(indices);

    this.texture = new THREE.CanvasTexture(this.canvas);
    this.texture.colorSpace = THREE.SRGBColorSpace;
    this.texture.flipY = false;
    this.texture.generateMipmaps = false;
    this.texture.minFilter = THREE.LinearFilter;
    this.uniforms = {
      map: { value: this.texture },
      lines: { value: new THREE.Vector2(resolution[0], resolution[1]) },
      time: { value: 0 },
      snow: { value: 0 },
      power: { value: 1 },
    };
    this.mesh = new THREE.Mesh(geometry, new THREE.ShaderMaterial({
      vertexShader: VERTEX, fragmentShader: FRAGMENT, uniforms: this.uniforms, toneMapped: false,
    }));
    this.mesh.name = "tv-screen";
    this.centre.copy(bl!).add(br!).add(tr!).add(tl!).multiplyScalar(0.25).addScaledVector(normal, bulge);

    const width = right.length();
    const height = up.length();
    this.hitbox = new THREE.Mesh(new THREE.BoxGeometry(width * 1.6, height * 1.6, width * 1.05), new THREE.MeshBasicMaterial());
    this.hitbox.position.copy(this.centre).addScaledVector(normal, -width * 0.5).addScaledVector(right.clone().normalize(), width * 0.14);
    this.hitbox.rotation.y = Math.atan2(-right.z, right.x);
    this.hitbox.layers.set(LAYER_SELECT);
    this.hitbox.userData.tv = this;
    this.head.copy(this.centre).add(new THREE.Vector3(0, height * 0.95, 0));
  }

  /** The canvas was painted: upload it. */
  refresh(): void {
    this.texture.needsUpdate = true;
  }

  /** Flood the picture with noise for `ms`, as a channel change does. */
  static(ms = 220): void {
    this.snowLeft = Math.max(this.snowLeft, ms);
  }

  update(dt: number): void {
    this.uniforms.time.value += dt / 1000;
    this.snowLeft = Math.max(0, this.snowLeft - dt);
    this.uniforms.snow.value = Math.min(1, this.snowLeft / 120) * 0.92;
  }

  /** Logical picture position under a ray, or null when the ray misses the glass. */
  pick(raycaster: THREE.Raycaster): [number, number] | null {
    const hit = raycaster.intersectObject(this.mesh, false)[0];
    if (!hit?.uv) return null;
    return [hit.uv.x * this.resolution[0], hit.uv.y * this.resolution[1]];
  }

  dispose(): void {
    this.texture.dispose();
    this.mesh.geometry.dispose();
    (this.mesh.material as THREE.Material).dispose();
  }
}
